// AI Copilot — conversational world building.
// The agent sees a compact scene summary and emits JSON actions that are
// executed through the same store -> broadcast -> persist pipeline human
// builders use, so its edits sync live to everyone and save automatically.
// Chat goes through the same-origin OpenRouter proxy (key stays server-side).

import { useWorldStore } from '../stores/worldStore';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';

// Always the same-origin Pages Function: the key lives server-side only. In
// dev, Vite proxies /api to the live functions (see vite.config.ts), so no
// provider key is ever read by client code (and none can be inlined).
const OPENROUTER_URL = '/api/openrouter/chat';
const MODEL = 'google/gemini-2.5-flash';

function authHeaders(): Record<string, string> {
  return {}; // key lives server-side; auth goes in gathrxrAuthHeader()
}

export interface CopilotAction {
  action: 'add_primitive' | 'search_models' | 'place_model' | 'generate_model' | 'move_object' | 'delete_object' | 'set_sky' | 'say';
  [key: string]: any;
}

export type ActionExecutor = (action: CopilotAction) => Promise<string>;

export function buildSceneSummary(): string {
  const s = useWorldStore.getState();
  const rows: string[] = [];
  Object.entries(s.primitives).forEach(([id, p]: [string, any]) => {
    rows.push(`${id} | primitive:${p.shape} "${p.name}" color=${p.color} pos=[${p.position.map((n: number) => n.toFixed(1))}] scale=[${p.scale.map((n: number) => n.toFixed(1))}]`);
  });
  Object.entries(s.contentItems).forEach(([id, c]: [string, any]) => {
    rows.push(`${id} | model "${c.filename || 'content'}" pos=[${c.position.map((n: number) => n.toFixed(1))}] scale=${c.scale}`);
  });
  Object.entries(s.lights).forEach(([id, l]: [string, any]) => {
    rows.push(`${id} | light:${l.type} color=${l.color} pos=[${l.position.map((n: number) => n.toFixed(1))}]`);
  });
  return rows.length ? rows.join('\n') : '(empty world)';
}

const SYSTEM_PROMPT = `You are the GathrXR build copilot. You build and edit 3D worlds by emitting JSON actions. Ground plane is y=0; people are ~1.7 units tall; keep builds human-scale and centered near the origin unless told otherwise. +x is right, +z is toward the default camera.

Respond ONLY with JSON: {"say": "<short friendly progress note>", "actions": [...], "done": true|false}
Set done=false ONLY when you need results back (after search_models) before continuing.

Actions:
- {"action":"add_primitive","shape":"box|sphere|cylinder|cone|plane|torus","name":"...","color":"#hex","position":[x,y,z],"scale":[x,y,z],"rotation":[x,y,z]} — rotation in radians, optional
- {"action":"search_models","query":"one or two words"} — searches a low-poly model library; results come back next turn. Search BROAD single nouns (chair, table, tree).
- {"action":"place_model","result_id":"<id from search results>","position":[x,y,z],"scale":1}
- {"action":"move_object","id":"<scene id>","position":[x,y,z]}
- {"action":"delete_object","id":"<scene id>"}
- {"action":"set_sky","prompt":"description of the sky/atmosphere"} — takes ~30s, use at most once
- {"action":"generate_model","description":"one specific object, visually detailed","position":[x,y,z],"scale":1} — generates a custom 3D model with AI. SLOW (~2 min each) and costs credits: use for hero/centerpiece objects or when the library has nothing suitable, and at most 2 per request unless asked.
Prefer library models for real-world things; primitives for architecture (floors, walls, stages, counters). Compose: a stage = wide flat box; a wall = thin tall box. Place multiple objects per turn. Vary rotation so scenes don't look gridded.`;

export async function runCopilot(
  userMessage: string,
  history: { role: string; content: string }[],
  execute: ActionExecutor,
  onSay: (text: string) => void,
  opts?: { meshyOnly?: boolean },
): Promise<{ role: string; content: string }[]> {
  const system = opts?.meshyOnly
    ? SYSTEM_PROMPT + '\n\nMODE: The builder chose AI-generated models ONLY. Do not use search_models or place_model. Use generate_model for every non-primitive object (sparingly — each takes ~2 minutes).'
    : SYSTEM_PROMPT;
  const messages: { role: string; content: string }[] = [
    { role: 'system', content: system },
    ...history,
    { role: 'user', content: `Current scene:\n${buildSceneSummary()}\n\nRequest: ${userMessage}` },
  ];

  for (let turn = 0; turn < 5; turn++) {
    const res = await fetch(OPENROUTER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(await gathrxrAuthHeader()) },
      body: JSON.stringify({ model: MODEL, messages, temperature: 0.4, max_tokens: 3000 }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error?.message || `Copilot request failed: ${res.status}`);
    }
    const data = await res.json();
    const raw: string = data.choices?.[0]?.message?.content || '';
    messages.push({ role: 'assistant', content: raw });

    let parsed: any;
    try {
      parsed = JSON.parse(raw.replace(/^```(json)?/m, '').replace(/```\s*$/m, '').trim());
    } catch {
      onSay(raw.slice(0, 300));
      break;
    }

    if (parsed.say) onSay(parsed.say);

    const results: string[] = [];
    for (const action of parsed.actions || []) {
      try {
        const result = await execute(action as CopilotAction);
        if (result) results.push(result);
      } catch (err: any) {
        const msg = `ERROR ${action.action}: ${err?.message || 'failed'}`;
        results.push(msg);
        onSay(`⚠ ${msg}`);
      }
    }

    if (parsed.done !== false) break;
    messages.push({ role: 'user', content: `Results:\n${results.join('\n') || '(none)'}\nContinue.` });
  }

  // Return trimmed history for the next exchange (drop system + scene bulk)
  return messages.slice(1).slice(-8);
}
